/**
 * 深色"现在"卡片（与 iOS HomeView.swift 的 NowCard 一致）：
 * 进行中只看时段日程（isTimedEvent）；下一项是有具体时间（时段、截止、到点，不含全天）里最早还没到的；还剩多久、进度条（跨天的按整个日程算）、下面列出今天要做的所有事、任务 x/y（select.todos）。
 */
import { useEffect, useMemo, useState } from "react";
import { hasClockTime, isAllDay, isTimedEvent, select, timeModeOf, timeText, useCalendar, useStore, wholeEndAt, wholeStartAt, type AgendaItem, type Child, type Todo } from "../../core";
import { BarProgress, C } from "../../ui";

/** 每 30 秒刷新一次的当前时间 */
function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

/** 跨天的日程可能还剩很久："25 分钟""3 小时""1 天 2 小时" */
export function remainText(ms: number): string {
  const m = Math.max(1, Math.floor(ms / 60_000));
  if (m < 180) return `${m} 分钟`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} 小时`;
  return h % 24 === 0 ? `${h / 24} 天` : `${Math.floor(h / 24)} 天 ${h % 24} 小时`;
}

/** 下一项什么时候："16:00 开始" / "21:00 前做完" / "20:30" */
function nextText(it: AgendaItem): string {
  const m = timeModeOf(it);
  return m === "due" ? `${it.startTime} 前做完` : m === "point" ? it.startTime : `${it.startTime} 开始`;
}

export function NowCard({ child }: { child: Child }) {
  const cal = useCalendar();
  const now = useNow();
  const today = cal.today();
  const day = useStore((s) => select.agendaDay(s, child.id, today));
  const agenda = useStore((s) => s.agenda);
  const assignments = useStore((s) => s.assignments);
  const checkins = useStore((s) => s.checkins);
  const todos = useMemo(() => select.todos({ agenda, assignments, checkins }, child.id, today), [agenda, assignments, checkins, child.id, today]);

  const all = day?.items ?? [];
  const items = all.filter(hasClockTime);
  const allDay = all.find(isAllDay);
  const current = items.filter(isTimedEvent).find((it) => it.startAt <= now && now < it.endAt);
  const next = items.find((it) => it.startAt > now);
  const item = current ?? next;

  // 今天要做的事做完了几项（学习、截止、到点、打开 App 的日程）
  const tasks = todos.length ? `任务 ${todos.filter((t) => t.done).length}/${todos.length}` : null;
  const progress = current ? (now - wholeStartAt(current)) / Math.max(1, wholeEndAt(current) - wholeStartAt(current)) : 0;

  return (
    <section className="home-now" aria-label="现在">
      <div className="home-now-top">
        <span>现在 · {cal.timeString(now)}</span>
        {current ? <em>进行中</em> : next ? <em>下一项</em> : null}
      </div>
      {item ? (
        <>
          <div className="home-now-main">
            <strong>{item.title}</strong>
            <span>{current ? `还剩 ${remainText(wholeEndAt(item) - now)}` : nextText(item)}</span>
          </div>
          <div className="home-now-bar">
            <BarProgress value={progress} height={8} track={C.dark3} fill={C.accent} />
            {tasks && <span>{tasks}</span>}
          </div>
        </>
      ) : (
        <>
          <div className="home-now-main">
            <strong>{items.length === 0 ? (allDay ? `今天全天：${allDay.title}` : day ? "今天没有安排" : "正在加载今天的安排…") : "今天的安排都结束啦"}</strong>
          </div>
          {tasks && <span className="home-now-tasks">{tasks}</span>}
        </>
      )}
      {todos.length > 0 && <TodoList todos={todos} highlight={item} />}
    </section>
  );
}

const STATUS: Record<Todo["status"], [string, string]> = {
  todo: ["待做", "var(--kp-disabled)"],
  doing: ["进行中", "var(--kp-accent)"],
  pending: ["等确认", "var(--kp-accent)"],
  done: ["完成", "var(--kp-success)"],
};

/** 今天要做的事全部列出来（时间 · 标题 · 状态），上面显示的那项高亮（与 iOS NowCard.todoList 一致） */
function TodoList({ todos, highlight }: { todos: Todo[]; highlight: AgendaItem | undefined }) {
  return (
    <ul className="home-now-todos">
      {todos.map((t) => {
        const [label, color] = STATUS[t.status];
        const on = highlight != null && t.item.eventId === highlight.eventId && t.item.startAt === highlight.startAt;
        return (
          <li key={`${t.item.eventId}@${t.item.startAt}`} className={`${on ? "on" : ""} ${t.done && t.status !== "pending" ? "done" : ""}`}>
            <time>{timeText(t.item)}</time>
            <span>{t.item.title}</span>
            <em style={{ color }}>{t.status === "done" && t.points != null ? `+${t.points}` : label}</em>
          </li>
        );
      })}
    </ul>
  );
}
